<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  currentChat: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['cancel', 'merged']);

const { t } = useI18n();

const duplicates = ref([]);
const selectedId = ref(null);
const reason = ref('');
const isLoading = ref(true);
const isSubmitting = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => {
    if (!value) emit('cancel');
  },
});

// Only the same contact in the same inbox can be folded together. Across two
// contacts the merged thread would have a single reply address and the other
// customer would never see the answer, so those are not offered at all.
const loadDuplicates = async () => {
  isLoading.value = true;
  try {
    const { data } = await ContactAPI.getConversations(
      props.currentChat.meta.sender.id
    );
    const list = data.payload || [];
    duplicates.value = list.filter(
      conversation =>
        conversation.id !== props.currentChat.id &&
        conversation.inbox_id === props.currentChat.inbox_id &&
        !conversation.additional_attributes?.merged_into_conversation_id
    );
  } catch (error) {
    duplicates.value = [];
  } finally {
    isLoading.value = false;
  }
};

const formatDate = value =>
  value
    ? new Date(value * 1000).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

const lastMessage = conversation =>
  conversation.messages?.[conversation.messages.length - 1]?.content || '';

const onSubmit = async () => {
  if (!selectedId.value) return;
  isSubmitting.value = true;
  try {
    await ConversationAPI.merge({
      baseConversationId: props.currentChat.id,
      mergeeConversationId: selectedId.value,
      reason: reason.value,
    });
    useAlert(t('MERGE_CONVERSATION.SUCCESS'));
    emit('merged');
    emit('cancel');
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('MERGE_CONVERSATION.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(loadDuplicates);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="() => emit('cancel')">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('MERGE_CONVERSATION.TITLE', { id: currentChat.id })"
        :header-content="$t('MERGE_CONVERSATION.DESC')"
      />
      <form class="w-full" @submit.prevent="onSubmit">
        <div class="flex flex-col w-full gap-4 p-8 pt-4">
          <div v-if="isLoading" class="text-sm text-n-slate-11">
            {{ $t('MERGE_CONVERSATION.LOADING') }}
          </div>

          <div v-else-if="!duplicates.length" class="text-sm text-n-slate-11">
            {{ $t('MERGE_CONVERSATION.EMPTY') }}
          </div>

          <div v-else class="flex flex-col gap-2">
            <label
              v-for="conversation in duplicates"
              :key="conversation.id"
              class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer border-n-weak hover:bg-n-alpha-1"
              :class="{ 'border-n-brand bg-n-alpha-1': selectedId === conversation.id }"
            >
              <input
                :id="`merge-${conversation.id}`"
                v-model="selectedId"
                type="radio"
                name="mergeeConversation"
                :value="conversation.id"
                class="mt-1"
              />
              <span class="flex flex-col min-w-0 gap-1">
                <span class="text-sm font-medium text-n-slate-12">
                  #{{ conversation.id }}
                  <span class="font-normal text-n-slate-11">
                    · {{ conversation.status }} ·
                    {{ formatDate(conversation.created_at) }}
                  </span>
                </span>
                <span class="text-sm truncate text-n-slate-11">
                  {{ lastMessage(conversation) }}
                </span>
              </span>
            </label>
          </div>

          <div v-if="duplicates.length" class="flex flex-col gap-1">
            <label for="merge-reason" class="text-sm text-n-slate-12">
              {{ $t('MERGE_CONVERSATION.REASON_LABEL') }}
            </label>
            <input
              id="merge-reason"
              v-model="reason"
              type="text"
              maxlength="1000"
              :placeholder="$t('MERGE_CONVERSATION.REASON_PLACEHOLDER')"
            />
          </div>

          <div class="flex items-center justify-end w-full gap-2">
            <NextButton
              faded
              slate
              type="button"
              :label="$t('MERGE_CONVERSATION.CANCEL')"
              @click="emit('cancel')"
            />
            <NextButton
              type="submit"
              :label="$t('MERGE_CONVERSATION.CONFIRM')"
              :is-loading="isSubmitting"
              :disabled="!selectedId || isSubmitting"
            />
          </div>
        </div>
      </form>
    </div>
  </woot-modal>
</template>
